import { useMemo, useState } from "react";
import {
  createGameCard,
  formatRoqeetGameSource,
  gameDefinitionToRqtDraft,
  gameDefinitionToRoqeetJson,
  lintGameDefinition,
  normalizeGameDefinition,
  parseRoqeetGameSource,
  roqeetRqtToGameDefinition,
  validateRoqeetGameDocument,
  type ContentRating,
  type GameAssetBinding,
  type GameAssetRegistryEntry,
  type GameCard,
  type GameClamp,
  type GameCondition,
  type GameConditionOp,
  type GameDefinition,
  type GameEndCondition,
  type GameGate,
  type GameLintIssue,
  type GameRule,
  type GameRuleAction,
  type GameStateFieldSchema,
  type GameStatBarBinding,
  type GameStatPanelBinding,
  type RoqeetGameDocumentIssue,
  type ShareMeta,
} from "@roqeet/core";
import { AlertTriangle, OctagonAlert, X } from "lucide-react";
import { saveGameCard } from "../../state/game-cards.js";
import { CodeEditor } from "./CodeEditor.js";
import { VersionHistoryPanel } from "./VersionHistoryPanel.js";
import { gameCardPath } from "../../state/version-actions.js";
import { useT } from "../../i18n/use-t.js";
import { useEscapeToClose } from "../use-escape-to-close.js";
import { currentSourceRqt, nextGameCardExtensionsForEditorSave } from "./game-card-editor-source.js";

/**
 * 데이터형 게임 카드 폼 편집기 — 결정 #29(제작 사다리: 폼 에디터 단). 사용자가 코드
 * 한 줄 없이 게임을 정의한다. 폼 입력 → `.rqg` 게임 문서 → GameDefinition
 * 호환 입력으로 낮아지고, saveGameCard가 compileGameDefinition으로 즉시 플레이
 * 가능한 모듈을 만든다.
 *
 * 보안(결정 #17): 모델이 바꿀 수 있는 키는 mutableKeys 화이트리스트로 제한되고,
 * 종료 조건은 선언적 술어뿐이라 임의 코드 주입이 없다. 컴파일러가 이를 강제한다.
 */

/** 초기 상태 한 행(폼 표현). value는 문자열로 입력받고 저장 시 타입 추론. */
interface StateRow {
  key: string;
  value: string;
  /** 표시용 타입 힌트. "list"는 배열(인벤토리 등), 나머지는 coerce로 추론. */
  type: "number" | "text" | "boolean" | "list";
}

const TONES = ["info", "success", "warning", "danger"] as const;
const CONDITION_OPS: GameConditionOp[] = ["lte", "gte", "lt", "gt", "eq", "neq", "truthy", "falsy"];
const ASSET_KINDS: GameAssetRegistryEntry["kind"][] = ["image", "audio", "sfx", "video", "other"];
const ASSET_SLOTS: GameAssetBinding["slot"][] = ["background", "sprite", "bgm", "sfx", "overlay"];
const ASSET_POSITIONS: NonNullable<GameAssetBinding["position"]>[] = ["left", "center", "right"];
type Translate = ReturnType<typeof useT>["t"];
type RqtSourceOrigin = "current-source" | "draft" | "edited";

export function GameCardEditor({
  card,
  onClose,
}: {
  card: GameCard | "new";
  onClose: () => void;
}) {
  const isNew = card === "new";
  const existing = isNew ? undefined : card;
  const def = existing?.definition;
  const { t } = useT();

  const [title, setTitle] = useState(def?.title ?? "");
  const [description, setDescription] = useState(def?.description ?? "");
  const [icon, setIcon] = useState(def?.icon ?? "game");
  const [instructions, setInstructions] = useState(def?.instructions ?? defaultInstructions(t));
  const [summary, setSummary] = useState(def?.stateSummaryTemplate ?? "");
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);

  const [stateRows, setStateRows] = useState<StateRow[]>(() =>
    initStateRows(def, t("gameEd.defaultLocation")),
  );
  const [clamps, setClamps] = useState<GameClamp[]>(() => def?.clamps ?? []);
  const [diceEnabled, setDiceEnabled] = useState<boolean>(!!def?.dice);
  const [dcKey, setDcKey] = useState<string>(def?.dice?.dcKey ?? "dc");
  const [endConds, setEndConds] = useState<GameEndCondition[]>(() => def?.endWhen ?? []);
  const [statBars, setStatBars] = useState<GameStatBarBinding[]>(() => def?.view?.statBars ?? []);
  const [statPanel, setStatPanel] = useState<GameStatPanelBinding[]>(
    () => def?.view?.statPanel ?? [],
  );
  const [inventoryKey, setInventoryKey] = useState(def?.view?.inventoryKey ?? "");
  const [inventoryTitle, setInventoryTitle] = useState(
    def?.view?.inventoryTitle ?? t("gameEd.defaultInventory"),
  );
  const [showChoices, setShowChoices] = useState(def?.view?.showChoices !== false);
  const [showDice, setShowDice] = useState(def?.view?.showDice ?? !!def?.dice);

  // 공유/출처 메타(결정 #23). 카드 IO가 이미 라운드트립하므로 여기서 입력만 채운다.
  const [author, setAuthor] = useState(existing?.share?.author ?? "");
  const [license, setLicense] = useState(existing?.share?.license ?? "");
  const [tagsText, setTagsText] = useState((existing?.share?.tags ?? []).join(", "));
  const [rating, setRating] = useState<ContentRating>(existing?.rating ?? "unrated");

  // 조건부 규칙(결정 #34). 폼 단에서도 편집 가능(아래 규칙 섹션). 코드 모드 편집도
  // applyDefinition에서 이 값을 갱신하므로 폼↔코드 전환·저장 시 손실이 없다.
  const [rules, setRules] = useState<GameRule[]>(() => def?.rules ?? []);
  const [gates, setGates] = useState<GameGate[]>(() => def?.gates ?? []);
  const [assets, setAssets] = useState<GameAssetRegistryEntry[]>(() => def?.assets ?? []);
  const [assetBindings, setAssetBindings] = useState<GameAssetBinding[]>(
    () => def?.assetBindings ?? [],
  );
  const [advancedDefinition, setAdvancedDefinition] = useState<Partial<GameDefinition>>(() =>
    pickAdvancedDefinition(def),
  );

  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  useEscapeToClose(onClose, !busy);

  // 제작 사다리 최상단(#30 코드 모드): 폼이 노출하지 않는 부분까지 `.rqg`
  // 게임 문서를 직접 편집한다. 폼↔코드 양방향 — 모드 전환 시 현재 상태를 서로 반영한다.
  const [mode, setMode] = useState<"form" | "code">("form");
  const [codeFormat, setCodeFormat] = useState<"json" | "rqt">("json");
  const [codeText, setCodeText] = useState("");
  const [rqtBaseDefinition, setRqtBaseDefinition] = useState<GameDefinition | null>(null);
  const [rqtSourceOrigin, setRqtSourceOrigin] = useState<RqtSourceOrigin | null>(null);

  // 현재 상태 행에서 키 목록(mutableKeys·바인딩 드롭다운 후보).
  const stateKeys = useMemo(
    () => stateRows.map((r) => r.key.trim()).filter(Boolean),
    [stateRows],
  );
  const assetKeys = useMemo(
    () => assets.map((asset) => asset.key.trim()).filter(Boolean),
    [assets],
  );

  /** 현재 폼 상태로 GameDefinition을 조립한다(검증 없음 — 직렬화/저장 공통). */
  const buildDefinition = (): GameDefinition => {
    const initialState = rowsToState(stateRows);
    const assetEntries = validAssets(assets);
    const sceneBindings = validAssetBindings(assetBindings);
    // mutableKeys: 상태 키 전체 + 주사위 난이도 키(dc)를 화이트리스트로(결정 #17).
    const stateSchema = mergeFormStateSchema(initialState, advancedDefinition.stateSchema);
    return {
      id: existing?.definition.id ?? "pending", // 저장 단계가 카드 id로 시드(신규).
      ...advancedDefinition,
      title: title.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(icon.trim() ? { icon: icon.trim() } : {}),
      initialState,
      instructions: instructions.trim(),
      ...(summary.trim() ? { stateSummaryTemplate: summary.trim() } : {}),
      stateSchema,
      ...(clamps.length ? { clamps: clamps.filter((c) => c.key.trim()) } : {}),
      ...(validRules(rules).length ? { rules: validRules(rules) } : {}),
      ...(validGates(gates).length ? { gates: validGates(gates) } : {}),
      ...(diceEnabled ? { dice: {} } : {}),
      ...(endConds.length ? { endWhen: endConds.filter((c) => c.key.trim()) } : {}),
      ...(assetEntries.length ? { assets: assetEntries } : {}),
      ...(sceneBindings.length ? { assetBindings: sceneBindings } : {}),
      view: {
        ...(statBars.length ? { statBars: statBars.filter((b) => b.valueKey.trim()) } : {}),
        ...(statPanel.length ? { statPanel: statPanel.filter((p) => p.key.trim()) } : {}),
        ...(inventoryKey.trim() ? { inventoryKey: inventoryKey.trim() } : {}),
        ...(inventoryKey.trim() && inventoryTitle.trim()
          ? { inventoryTitle: inventoryTitle.trim() }
          : {}),
        showChoices,
        ...(showDice ? { showDice: true } : {}),
      },
    };
  };

  /** GameDefinition을 폼 상태로 펼친다(코드→폼 전환 시 편집 반영). */
  const applyDefinition = (d: GameDefinition) => {
    setTitle(d.title ?? "");
    setDescription(d.description ?? "");
    setIcon(d.icon ?? "game");
    setInstructions(d.instructions ?? "");
    setSummary(d.stateSummaryTemplate ?? "");
    setStateRows(initStateRows(d, t("gameEd.defaultLocation")));
    setClamps(d.clamps ?? []);
    setRules(d.rules ?? []);
    setGates(d.gates ?? []);
    setAssets(d.assets ?? []);
    setAssetBindings(d.assetBindings ?? []);
    setDiceEnabled(!!d.dice);
    setDcKey(d.dice?.dcKey ?? "dc");
    setEndConds(d.endWhen ?? []);
    setStatBars(d.view?.statBars ?? []);
    setStatPanel(d.view?.statPanel ?? []);
    setInventoryKey(d.view?.inventoryKey ?? "");
    setInventoryTitle(d.view?.inventoryTitle ?? t("gameEd.defaultInventory"));
    setShowChoices(d.view?.showChoices !== false);
    setShowDice(d.view?.showDice ?? !!d.dice);
    setAdvancedDefinition(pickAdvancedDefinition(d));
  };

  /** 폼 → 코드: 현재 정의를 보기 좋은 JSON으로 직렬화해 코드 편집으로 전환. */
  const switchToCode = () => {
    setError(null);
    const def = buildDefinition();
    setCodeFormat("json");
    setRqtBaseDefinition(null);
    setRqtSourceOrigin(null);
    setCodeText(formatRoqeetGameSource(def));
    setMode("code");
  };

  const parseRqtEditorDefinition = (text = codeText): GameDefinition =>
    roqeetRqtToGameDefinition(
      text,
      "editor.rqt",
      rqtBaseDefinition ? { baseDefinition: rqtBaseDefinition } : undefined,
    );

  const documentIssues = useMemo<RoqeetGameDocumentIssue[]>(() => {
    if (mode !== "code") return [];
    if (codeFormat === "rqt") {
      try {
        parseRqtEditorDefinition();
        return [{
          severity: "info",
          code: "RQT_BEST_EFFORT",
          message: "RQT DSL is a human-readable authoring view. Advanced JSON fields are preserved from the current editor document when available.",
          path: "$",
        }];
      } catch (e) {
        return [{
          severity: "error",
          code: "RQT_PARSE",
          message: e instanceof Error ? e.message : String(e),
          path: "$",
        }];
      }
    }
    try {
      return validateRoqeetGameDocument(JSON.parse(codeText));
    } catch {
      return [{
        severity: "error",
        code: "RGD_JSON",
        message: t("gameEd.docJsonSyntax"),
        path: "$",
      }];
    }
  }, [mode, codeFormat, codeText, rqtBaseDefinition, t]);
  const hasDocumentErrors = documentIssues.some((issue) => issue.severity === "error");

  /** 코드 → 폼: .rqg을 검증·파싱해 폼에 반영. 실패 시 코드 모드 유지 + 에러. */
  const switchToForm = (): boolean => {
    if (hasDocumentErrors) {
      setError(t("gameEd.docHasErrors"));
      return false;
    }
    try {
      const definition = codeFormat === "rqt"
        ? parseRqtEditorDefinition()
        : parseRoqeetGameSource(codeText).definition;
      applyDefinition(definition);
      setError(null);
      setMode("form");
      setRqtSourceOrigin(null);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return false;
    }
  };

  // 제작 린트(결정 #30): 폼 상태(또는 파싱 가능한 코드)로 정의를 만들어 "조용한
  // 오작동"을 경고로 잡는다. 컴파일러 화이트리스트(보안)와 별개의 제작 보조 장치.
  const lintIssues = useMemo<GameLintIssue[]>(() => {
    try {
      const def = mode === "code"
        ? codeFormat === "rqt"
          ? parseRqtEditorDefinition()
          : parseRoqeetGameSource(codeText).definition
        : buildDefinition();
      const normalizedIssues: GameLintIssue[] = normalizeGameDefinition(def).diagnostics.map((issue) => ({
        severity: issue.severity,
        message: issue.path ? `${issue.path}: ${issue.message}` : issue.message,
        ...(issue.path ? { field: issue.path } : {}),
      }));
      return [...lintGameDefinition(def), ...normalizedIssues];
    } catch {
      // 코드 모드에서 JSON이 아직 안 깨끗하면 린트는 생략(저장 시 파싱 에러로 안내).
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mode,
    codeFormat,
    codeText,
    rqtBaseDefinition,
    title,
    instructions,
    summary,
    stateRows,
    clamps,
    rules,
    gates,
    assets,
    assetBindings,
    advancedDefinition,
    diceEnabled,
    dcKey,
    endConds,
    statBars,
    statPanel,
    inventoryKey,
    inventoryTitle,
    showChoices,
    showDice,
  ]);

  const save = async (closeAfter = true) => {
    setError(null);

    // 코드 모드면 편집한 .rqg을 먼저 파싱·검증해 폼에 반영(실패 시 저장 중단).
    let definition: GameDefinition;
    let document = gameDefinitionToRoqeetJson(buildDefinition());
    if (mode === "code") {
      if (hasDocumentErrors) {
        setError(t("gameEd.docHasErrors"));
        return;
      }
      try {
        if (codeFormat === "rqt") {
          definition = parseRqtEditorDefinition();
          document = gameDefinitionToRoqeetJson(definition);
        } else {
          const source = parseRoqeetGameSource(codeText);
          definition = source.definition;
          document = source.document;
        }
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
        return;
      }
    } else {
      const titleText = title.trim();
      if (!titleText) {
        setError(t("gameEd.errTitleRequired"));
        return;
      }
      definition = buildDefinition();
      document = gameDefinitionToRoqeetJson(definition);
    }

    setBusy(true);
    try {
      // 공유/출처 메타 조립(결정 #23). 값이 있는 필드만 담는다(빈 share 방지).
      const tags = tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const share: ShareMeta = {
        ...(author.trim() ? { author: author.trim() } : {}),
        ...(license.trim() ? { license: license.trim() } : {}),
        ...(tags.length ? { tags } : {}),
      };
      const hasShare = Object.keys(share).length > 0;
      const extensions = nextGameCardExtensionsForEditorSave(existing?.extensions, mode, codeFormat, codeText, {
        rqtSourceStatus: mode === "code" && codeFormat === "rqt" &&
            (rqtSourceOrigin === "current-source" || rqtSourceOrigin === "edited")
          ? "current"
          : "stale",
      });
      const hasExtensions = extensions !== undefined;

      const base: GameCard = existing
        ? {
            ...existing,
            ...(hasExtensions ? { extensions } : {}),
            enabled,
            definition: { ...definition, id: existing.definition.id },
            document,
          }
        : createGameCard({ definition, document, enabled, ...(hasExtensions ? { extensions } : {}) });
      const next: GameCard = {
        ...base,
        ...(hasShare ? { share } : {}),
        ...(rating !== "unrated" ? { rating } : {}),
      };
      await saveGameCard(next);
      if (closeAfter) onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="flex max-h-screen max-h-[100dvh] w-full max-w-2xl flex-col overflow-hidden bg-bg sm:max-h-[calc(100vh-2rem)] sm:max-h-[calc(100dvh-2rem)] sm:rounded-2xl sm:border sm:border-line">
        {/* 헤더 */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <h2 className="text-sm font-semibold text-ink">
            {isNew ? t("gameEd.newTitle") : t("gameEd.editTitle")}
          </h2>
          <div className="flex items-center gap-2">
            {/* 폼/코드 모드 전환(#30). 코드 모드는 .rqg 직접 편집. */}
            <div className="flex overflow-hidden rounded-lg border border-line text-xs">
              <button
                className={[
                  "px-2 py-1",
                  mode === "form" ? "bg-accent text-white" : "text-ink-soft hover:bg-bg-raised",
                ].join(" ")}
                onClick={() => {
                  if (mode === "code") switchToForm();
                }}
              >
                {t("gameEd.modeForm")}
              </button>
              <button
                className={[
                  "px-2 py-1",
                  mode === "code" ? "bg-accent text-white" : "text-ink-soft hover:bg-bg-raised",
                ].join(" ")}
                onClick={() => {
                  if (mode === "form") switchToCode();
                }}
                title={t("gameEd.modeCodeTitle")}
              >
                {t("gameEd.modeCode")}
              </button>
            </div>
            <button
              className="rounded p-1 text-ink-faint hover:bg-bg-raised hover:text-ink"
              onClick={onClose}
              aria-label={t("misc.close")}
              title={t("misc.close")}
            >
              <X size={17} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* 본문(스크롤) — 코드 모드 */}
        {mode === "code" ? (
          <div className="flex min-h-0 flex-1 flex-col gap-2 p-4">
            <div className="rounded-lg border border-line bg-bg-soft px-3 py-2">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-ink">Code format</div>
                  <p className="mt-1 text-xs text-ink-faint">
                    {codeFormat === "json"
                      ? "Editing the official .rqg document. Switch to .rqt DSL for advanced text authoring."
                      : "Editing advanced .rqt DSL text. Import is parsed into the .rqg document before preview/save."}
                  </p>
                </div>
                <div className="flex shrink-0 overflow-hidden rounded-lg border border-line text-xs">
                <button
                  className={[
                    "px-3 py-1.5",
                    codeFormat === "json" ? "bg-accent text-white" : "text-ink-soft hover:bg-bg-raised",
                  ].join(" ")}
                  onClick={() => {
                    if (codeFormat === "json") return;
                    try {
                      const def = parseRqtEditorDefinition();
                      setCodeFormat("json");
                      setRqtBaseDefinition(null);
                      setRqtSourceOrigin(null);
                      setCodeText(formatRoqeetGameSource(def));
                      setError(null);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    }
                  }}
                >
                  .rqg
                </button>
                <button
                  className={[
                    "px-3 py-1.5",
                    codeFormat === "rqt" ? "bg-accent text-white" : "text-ink-soft hover:bg-bg-raised",
                  ].join(" ")}
                  onClick={() => {
                    if (codeFormat === "rqt") return;
                    try {
                      const source = parseRoqeetGameSource(codeText);
                      const savedRqg = existing ? formatRoqeetGameSource(existing.definition) : undefined;
                      const preservedSource = savedRqg === codeText
                        ? currentSourceRqt(existing?.extensions)
                        : undefined;
                      setCodeFormat("rqt");
                      setRqtBaseDefinition(source.definition);
                      setRqtSourceOrigin(preservedSource ? "current-source" : "draft");
                      setCodeText(preservedSource ?? gameDefinitionToRqtDraft(source.definition, source.document));
                      setError(null);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    }
                  }}
                >
                  .rqt DSL
                </button>
              </div>
              </div>
            </div>
            {documentIssues.length > 0 && (
              <DocumentIssueList title={t("gameEd.docValidationTitle")} issues={documentIssues} />
            )}
            <CodeEditor
              value={codeText}
              onChange={(nextText) => {
                if (codeFormat === "rqt" && nextText !== codeText && rqtSourceOrigin !== "edited") {
                  setRqtSourceOrigin("edited");
                }
                setCodeText(nextText);
              }}
              language={codeFormat === "json" ? "JSON" : "RQT DSL"}
              aiKind={codeFormat === "json" ? "rqg-json" : undefined}
              onSave={() => save(false)}
              fill
            />
            {error && (
              <div className="flex items-center gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-600">
                <OctagonAlert size={16} aria-hidden="true" />
                {t(error as any)}
              </div>
            )}
            {/* 버전 기록(로컬 git, 결정 #30): 저장된 카드만 추적(새 카드는 id 없음). */}
            {existing && (
              <VersionHistoryPanel
                path={gameCardPath(existing.id)}
                value={codeText}
                onRestore={setCodeText}
              />
            )}
          </div>
        ) : (
        /* 본문(스크롤) — 폼 모드 */
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4 scroll-touch">
          {/* 기본 정보 */}
          <Field label={t("gameEd.fieldTitle")}>
            <input
              className={inputCls}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("gameEd.phTitle")}
            />
          </Field>
          <Field label={t("gameEd.fieldDescription")}>
            <input
              className={inputCls}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("gameEd.phDescription")}
            />
          </Field>
          <Field label={t("gameEd.fieldIcon")}>
            <input
              className={inputCls}
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="game / dice / mask / key / compass"
            />
          </Field>

          {/* AI 서술 가이드 */}
          <Field
            label={t("gameEd.fieldInstructions")}
            hint={t("gameEd.hintInstructions")}
          >
            <textarea
              className={`${inputCls} min-h-[140px] resize-y font-mono text-xs`}
              value={instructions}
              onChange={(event) => setInstructions(event.target.value)}
            />
          </Field>
          <Field
            label={t("gameEd.fieldSummary")}
            hint={t("gameEd.hintSummary")}
          >
            <input
              className={`${inputCls} font-mono text-xs`}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder={t("gameEd.phSummary")}
            />
          </Field>

          {/* 초기 상태 */}
          <div>
            <SectionTitle
              title={t("gameEd.initialState")}
              hint={t("gameEd.initialStateHint")}
            />
            <div className="space-y-2">
              {stateRows.map((row, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className={`${inputCls} flex-1`}
                    value={row.key}
                    placeholder={t("gameEd.phKeyHp")}
                    onChange={(e) => updateRow(stateRows, setStateRows, i, { key: e.target.value })}
                  />
                  <select
                    className={`${inputCls} w-24`}
                    value={row.type}
                    onChange={(e) =>
                      updateRow(stateRows, setStateRows, i, {
                        type: e.target.value as StateRow["type"],
                      })
                    }
                  >
                    <option value="number">{t("gameEd.typeNumber")}</option>
                    <option value="text">{t("gameEd.typeText")}</option>
                    <option value="boolean">{t("gameEd.typeBoolean")}</option>
                    <option value="list">{t("gameEd.typeList")}</option>
                  </select>
                  {row.type === "list" ? (
                    <span className="flex-1 text-xs text-ink-faint">{t("gameEd.emptyListHint")}</span>
                  ) : row.type === "boolean" ? (
                    <select
                      className={`${inputCls} flex-1`}
                      value={row.value}
                      onChange={(e) =>
                        updateRow(stateRows, setStateRows, i, { value: e.target.value })
                      }
                    >
                      <option value="false">{t("gameEd.boolFalse")}</option>
                      <option value="true">{t("gameEd.boolTrue")}</option>
                    </select>
                  ) : (
                    <input
                      className={`${inputCls} flex-1`}
                      value={row.value}
                      placeholder={t("gameEd.phValue")}
                      onChange={(e) =>
                        updateRow(stateRows, setStateRows, i, { value: e.target.value })
                      }
                    />
                  )}
                  <button
                    className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                    onClick={() => setStateRows(stateRows.filter((_, j) => j !== i))}
                    aria-label={t("gameEd.deleteVariable")}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() =>
                  setStateRows([...stateRows, { key: "", value: "", type: "number" }])
                }
              >
                {t("gameEd.addVariable")}
              </button>
            </div>
          </div>

          <AdvancedDetails
            title="Advanced controls"
            hint="State permissions, clamps, conditional rules, prompt gates, dice, and asset bindings."
          >
          {/* 주사위 */}
          <div>
            <SectionTitle title={t("gameEd.dice")} hint={"AI가 @주사위 d20 명령을 내면 시드 RNG로 굴립니다."} />
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={diceEnabled}
                onChange={(e) => setDiceEnabled(e.target.checked)}
              />
              {t("gameEd.useDice")}
            </label>
            {diceEnabled && (
              <div className="mt-2">
                <Field
                  label={t("gameEd.dcKey")}
                  hint={"AI가 @설정 dc = 15 후 굴리면 결과를 이 값과 비교해 성공/실패를 정합니다."}
                >
                  <input
                    className={inputCls}
                    value={dcKey}
                    onChange={(e) => setDcKey(e.target.value)}
                    placeholder="dc"
                  />
                </Field>
              </div>
            )}
          </div>

          {/* 클램프 */}
          <div>
            <SectionTitle title={t("gameEd.clamps")} hint={t("gameEd.clampsHint")} />
            <div className="space-y-2">
              {clamps.map((c, i) => (
                <div key={i} className="flex items-center gap-2">
                  <KeySelect
                    value={c.key}
                    keys={stateKeys}
                    placeholder={t("gameEd.phKey")}
                    onChange={(key) => updateAt(clamps, setClamps, i, { key })}
                  />
                  <input
                    className={`${inputCls} w-20`}
                    value={c.min ?? ""}
                    placeholder={t("gameEd.min")}
                    inputMode="numeric"
                    onChange={(e) =>
                      updateAt(clamps, setClamps, i, { min: parseOptNum(e.target.value) })
                    }
                  />
                  <input
                    className={`${inputCls} w-20`}
                    value={c.max ?? ""}
                    placeholder={t("gameEd.max")}
                    inputMode="numeric"
                    onChange={(e) =>
                      updateAt(clamps, setClamps, i, { max: parseOptNum(e.target.value) })
                    }
                  />
                  <button
                    className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                    onClick={() => setClamps(clamps.filter((_, j) => j !== i))}
                    aria-label={t("common.delete")}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() => setClamps([...clamps, { key: stateKeys[0] ?? "" }])}
              >
                {t("gameEd.addClamp")}
              </button>
            </div>
          </div>

          {/* 조건부 규칙(결정 #34) */}
          <div>
            <SectionTitle
              title={t("gameEd.rules")}
              hint={t("gameEd.rulesHint")}
            />
            <div className="space-y-3">
              {rules.map((rule, ri) => (
                <div key={ri} className="rounded-lg border border-line bg-bg-soft p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-ink-faint">
                      {t("gameEd.ruleNumber", { count: ri + 1 })}
                    </span>
                    <button
                      className="rounded px-2 py-0.5 text-xs text-red-400 hover:bg-bg-raised"
                      onClick={() => setRules(rules.filter((_, j) => j !== ri))}
                    >
                      {t("gameEd.deleteRule")}
                    </button>
                  </div>

                  {/* when: 조건들 */}
                  <div className="mb-2">
                    <div className="mb-1 text-[11px] font-medium text-ink-soft">
                      {t("gameEd.whenAll")}
                    </div>
                    <div className="space-y-1.5">
                      {rule.when.map((cond, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <KeySelect
                            value={cond.key}
                            keys={stateKeys}
                            placeholder={t("gameEd.phKey")}
                            onChange={(key) =>
                              setRules(patchCond(rules, ri, ci, { key }))
                            }
                          />
                          <select
                            className={`${inputCls} w-28`}
                            value={cond.op}
                            onChange={(e) =>
                              setRules(
                                patchCond(rules, ri, ci, { op: e.target.value as GameConditionOp }),
                              )
                            }
                          >
                            {CONDITION_OPS.map((op) => (
                              <option key={op} value={op}>
                                {conditionOpLabel(op, t)}
                              </option>
                            ))}
                          </select>
                          {cond.op !== "truthy" && cond.op !== "falsy" && (
                            <input
                              className={`${inputCls} w-20`}
                              value={String(cond.value ?? "")}
                              placeholder={t("gameEd.phValue")}
                              onChange={(e) =>
                                setRules(
                                  patchCond(rules, ri, ci, { value: coerceValue(e.target.value) }),
                                )
                              }
                            />
                          )}
                          <button
                            className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                            onClick={() => setRules(removeCond(rules, ri, ci))}
                            aria-label={t("gameEd.deleteCondition")}
                          >
                            <X size={14} aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                      <button
                        className={addBtnCls}
                        onClick={() =>
                          setRules(addCond(rules, ri, { key: stateKeys[0] ?? "", op: "lte", value: 0 }))
                        }
                      >
                        {t("gameEd.addCondition")}
                      </button>
                    </div>
                  </div>

                  {/* then: 효과들 */}
                  <div>
                    <div className="mb-1 text-[11px] font-medium text-ink-soft">
                      {t("gameEd.thenDo")}
                    </div>
                    <div className="space-y-1.5">
                      {rule.then.map((action, ai) => (
                        <div key={ai} className="flex items-center gap-2">
                          <select
                            className={`${inputCls} w-20`}
                            value={action.op}
                            onChange={(e) =>
                              setRules(
                                patchAction(rules, ri, ai, {
                                  op: e.target.value as GameRuleAction["op"],
                                }),
                              )
                            }
                          >
                            <option value="set">{t("gameEd.actionSet")}</option>
                            <option value="inc">{t("gameEd.actionInc")}</option>
                            <option value="dec">{t("gameEd.actionDec")}</option>
                          </select>
                          <input
                            className={`${inputCls} flex-1`}
                            value={action.key}
                            placeholder={t("gameEd.phKeyStatus")}
                            onChange={(e) =>
                              setRules(patchAction(rules, ri, ai, { key: e.target.value }))
                            }
                          />
                          <input
                            className={`${inputCls} w-24`}
                            value={String(action.value ?? "")}
                            placeholder={t("gameEd.phValue")}
                            onChange={(e) =>
                              setRules(
                                patchAction(rules, ri, ai, { value: coerceValue(e.target.value) }),
                              )
                            }
                          />
                          <button
                            className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                            onClick={() => setRules(removeAction(rules, ri, ai))}
                            aria-label={t("gameEd.deleteEffect")}
                          >
                            <X size={14} aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                      <button
                        className={addBtnCls}
                        onClick={() =>
                          setRules(addAction(rules, ri, { op: "set", key: "", value: "" }))
                        }
                      >
                        {t("gameEd.addEffect")}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() => setRules([...rules, { when: [], then: [{ op: "set", key: "", value: "" }] }])}
              >
                {t("gameEd.addRule")}
              </button>
            </div>
          </div>

          {/* 조건부 AI 지시문/선택지 제어(게이트) */}
          <div>
            <SectionTitle
              title="조건부 AI 지시문 / 선택지 제어"
              hint="조건이 맞을 때 다음 턴 프롬프트에 문구를 주입합니다. 상태는 바꾸지 않고, AI의 말투·금지 행동·선택지 구성을 잡습니다."
            />
            <div className="space-y-3">
              {gates.map((gate, gi) => (
                <div key={gi} className="rounded-lg border border-line bg-bg-soft p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <span className="text-xs font-semibold text-ink-faint">
                      지시문 {gi + 1}
                    </span>
                    <button
                      className="rounded px-2 py-0.5 text-xs text-red-400 hover:bg-bg-raised"
                      onClick={() => setGates(gates.filter((_, j) => j !== gi))}
                    >
                      지시문 삭제
                    </button>
                  </div>

                  <div className="mb-2">
                    <div className="mb-1 text-[11px] font-medium text-ink-soft">
                      {t("gameEd.whenAll")}
                    </div>
                    <div className="space-y-1.5">
                      {gate.when.map((cond, ci) => (
                        <div key={ci} className="flex items-center gap-2">
                          <KeySelect
                            value={cond.key}
                            keys={stateKeys}
                            placeholder={t("gameEd.phKey")}
                            onChange={(key) =>
                              setGates(patchGateCond(gates, gi, ci, { key }))
                            }
                          />
                          <select
                            className={`${inputCls} w-28`}
                            value={cond.op}
                            onChange={(e) =>
                              setGates(
                                patchGateCond(gates, gi, ci, {
                                  op: e.target.value as GameConditionOp,
                                }),
                              )
                            }
                          >
                            {CONDITION_OPS.map((op) => (
                              <option key={op} value={op}>
                                {conditionOpLabel(op, t)}
                              </option>
                            ))}
                          </select>
                          {cond.op !== "truthy" && cond.op !== "falsy" && (
                            <input
                              className={`${inputCls} w-20`}
                              value={String(cond.value ?? "")}
                              placeholder={t("gameEd.phValue")}
                              onChange={(e) =>
                                setGates(
                                  patchGateCond(gates, gi, ci, {
                                    value: coerceValue(e.target.value),
                                  }),
                                )
                              }
                            />
                          )}
                          <button
                            className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                            onClick={() => setGates(removeGateCond(gates, gi, ci))}
                            aria-label={t("gameEd.deleteCondition")}
                          >
                            <X size={14} aria-hidden="true" />
                          </button>
                        </div>
                      ))}
                      <button
                        className={addBtnCls}
                        onClick={() =>
                          setGates(
                            addGateCond(gates, gi, {
                              key: stateKeys[0] ?? "",
                              op: "lte",
                              value: 0,
                            }),
                          )
                        }
                      >
                        {t("gameEd.addCondition")}
                      </button>
                    </div>
                  </div>

                  <Field
                    label="AI에게 추가로 주입할 지시문"
                    hint="@(hp)처럼 상태 키를 넣으면 현재 값으로 치환됩니다."
                  >
                    <textarea
                      className={`${inputCls} min-h-[88px] resize-y`}
                      value={gate.inject}
                      placeholder="예: HP가 낮으면 무모한 공격 대신 치료/후퇴 선택지를 하나 포함하세요."
                      onChange={(e) => setGates(patchGate(gates, gi, { inject: e.target.value }))}
                    />
                  </Field>
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() =>
                  setGates([
                    ...gates,
                    {
                      when: [],
                      inject:
                        "선택지는 보통 3개를 제안하고, 하나는 안전한 행동, 하나는 위험한 행동, 하나는 정보를 얻는 행동으로 구성하세요.",
                    },
                  ])
                }
              >
                + AI 지시문 추가
              </button>
            </div>
          </div>

          {/* 에셋-장면 바인딩(결정 #27/#29). 상태 조건이 화면 에셋을 결정한다. */}
          <div>
            <SectionTitle
              title="에셋 / 장면 바인딩"
              hint="에셋 키를 등록하고, 상태 조건에 따라 배경·스프라이트·오디오 슬롯을 연결합니다. 현재 화면 미리보기는 배경과 스프라이트를 즉시 렌더합니다."
            />
            <div className="space-y-3">
              <div className="rounded-lg border border-line bg-bg-soft p-2">
                <div className="mb-2 text-xs font-semibold text-ink-faint">에셋 레지스트리</div>
                <div className="space-y-2">
                  {assets.map((asset, i) => (
                    <div
                      key={i}
                      className="grid gap-2 rounded-lg border border-line/70 bg-bg px-2 py-2 sm:grid-cols-[minmax(0,1fr)_8rem_minmax(0,1.2fr)_minmax(0,1fr)_auto]"
                    >
                      <input
                        className={inputCls}
                        value={asset.key}
                        placeholder="키 (예: bg.library)"
                        onChange={(e) => updateAt(assets, setAssets, i, { key: e.target.value })}
                      />
                      <select
                        className={inputCls}
                        value={asset.kind}
                        onChange={(e) =>
                          updateAt(assets, setAssets, i, {
                            kind: e.target.value as GameAssetRegistryEntry["kind"],
                          })
                        }
                      >
                        {ASSET_KINDS.map((kind) => (
                          <option key={kind} value={kind}>
                            {assetKindLabel(kind)}
                          </option>
                        ))}
                      </select>
                      <input
                        className={inputCls}
                        value={asset.assetId ?? ""}
                        placeholder="assetId 또는 패키지 경로"
                        onChange={(e) =>
                          updateAt(assets, setAssets, i, { assetId: e.target.value })
                        }
                      />
                      <input
                        className={inputCls}
                        value={asset.label ?? ""}
                        placeholder="표시 이름"
                        onChange={(e) => updateAt(assets, setAssets, i, { label: e.target.value })}
                      />
                      <button
                        className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                        onClick={() => setAssets(assets.filter((_, j) => j !== i))}
                        aria-label="에셋 삭제"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </div>
                  ))}
                  <button
                    className={addBtnCls}
                    onClick={() =>
                      setAssets([
                        ...assets,
                        { key: `asset.${assets.length + 1}`, kind: "image", assetId: "" },
                      ])
                    }
                  >
                    + 에셋 추가
                  </button>
                </div>
              </div>

              <div className="rounded-lg border border-line bg-bg-soft p-2">
                <div className="mb-2 text-xs font-semibold text-ink-faint">상태 기반 장면 바인딩</div>
                <div className="space-y-3">
                  {assetBindings.map((binding, bi) => (
                    <div key={bi} className="rounded-lg border border-line/70 bg-bg px-2 py-2">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-semibold text-ink-faint">
                          바인딩 {bi + 1}
                        </span>
                        <button
                          className="rounded px-2 py-0.5 text-xs text-red-400 hover:bg-bg-raised"
                          onClick={() =>
                            setAssetBindings(assetBindings.filter((_, j) => j !== bi))
                          }
                        >
                          바인딩 삭제
                        </button>
                      </div>

                      <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_8rem_minmax(0,1fr)_minmax(0,1fr)]">
                        <input
                          className={inputCls}
                          value={binding.id}
                          placeholder="id (예: bg.library)"
                          onChange={(e) =>
                            setAssetBindings(patchAssetBinding(assetBindings, bi, { id: e.target.value }))
                          }
                        />
                        <select
                          className={inputCls}
                          value={binding.slot}
                          onChange={(e) =>
                            setAssetBindings(
                              patchAssetBinding(assetBindings, bi, {
                                slot: e.target.value as GameAssetBinding["slot"],
                              }),
                            )
                          }
                        >
                          {ASSET_SLOTS.map((slot) => (
                            <option key={slot} value={slot}>
                              {assetSlotLabel(slot)}
                            </option>
                          ))}
                        </select>
                        <KeySelect
                          value={binding.assetKey ?? ""}
                          keys={assetKeys}
                          placeholder="에셋 키"
                          onChange={(assetKey) =>
                            setAssetBindings(patchAssetBinding(assetBindings, bi, { assetKey }))
                          }
                        />
                        <input
                          className={inputCls}
                          value={binding.assetId ?? ""}
                          placeholder="직접 assetId(선택)"
                          onChange={(e) =>
                            setAssetBindings(
                              patchAssetBinding(assetBindings, bi, { assetId: e.target.value }),
                            )
                          }
                        />
                      </div>

                      {binding.slot === "sprite" && (
                        <div className="mt-2 grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_8rem]">
                          <input
                            className={inputCls}
                            value={binding.characterId ?? ""}
                            placeholder="캐릭터 id (예: yuna)"
                            onChange={(e) =>
                              setAssetBindings(
                                patchAssetBinding(assetBindings, bi, { characterId: e.target.value }),
                              )
                            }
                          />
                          <input
                            className={inputCls}
                            value={binding.emotion ?? ""}
                            placeholder="표정/감정 (예: smile)"
                            onChange={(e) =>
                              setAssetBindings(
                                patchAssetBinding(assetBindings, bi, { emotion: e.target.value }),
                              )
                            }
                          />
                          <select
                            className={inputCls}
                            value={binding.position ?? ""}
                            onChange={(e) =>
                              setAssetBindings(
                                patchAssetBinding(assetBindings, bi, {
                                  position: e.target.value
                                    ? (e.target.value as NonNullable<GameAssetBinding["position"]>)
                                    : undefined,
                                }),
                              )
                            }
                          >
                            <option value="">위치 없음</option>
                            {ASSET_POSITIONS.map((position) => (
                              <option key={position} value={position}>
                                {assetPositionLabel(position)}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div className="mt-2">
                        <div className="mb-1 text-[11px] font-medium text-ink-soft">
                          {t("gameEd.whenAll")}
                        </div>
                        <div className="space-y-1.5">
                          {(binding.when ?? []).map((cond, ci) => (
                            <div key={ci} className="flex items-center gap-2">
                              <KeySelect
                                value={cond.key}
                                keys={stateKeys}
                                placeholder={t("gameEd.phKey")}
                                onChange={(key) =>
                                  setAssetBindings(
                                    patchAssetBindingCond(assetBindings, bi, ci, { key }),
                                  )
                                }
                              />
                              <select
                                className={`${inputCls} w-28`}
                                value={cond.op}
                                onChange={(e) =>
                                  setAssetBindings(
                                    patchAssetBindingCond(assetBindings, bi, ci, {
                                      op: e.target.value as GameConditionOp,
                                    }),
                                  )
                                }
                              >
                                {CONDITION_OPS.map((op) => (
                                  <option key={op} value={op}>
                                    {conditionOpLabel(op, t)}
                                  </option>
                                ))}
                              </select>
                              {cond.op !== "truthy" && cond.op !== "falsy" && (
                                <input
                                  className={`${inputCls} w-20`}
                                  value={String(cond.value ?? "")}
                                  placeholder={t("gameEd.phValue")}
                                  onChange={(e) =>
                                    setAssetBindings(
                                      patchAssetBindingCond(assetBindings, bi, ci, {
                                        value: coerceValue(e.target.value),
                                      }),
                                    )
                                  }
                                />
                              )}
                              <button
                                className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                                onClick={() =>
                                  setAssetBindings(removeAssetBindingCond(assetBindings, bi, ci))
                                }
                                aria-label={t("gameEd.deleteCondition")}
                              >
                                <X size={14} aria-hidden="true" />
                              </button>
                            </div>
                          ))}
                          <button
                            className={addBtnCls}
                            onClick={() =>
                              setAssetBindings(
                                addAssetBindingCond(assetBindings, bi, {
                                  key: stateKeys[0] ?? "",
                                  op: "eq",
                                  value: "",
                                }),
                              )
                            }
                          >
                            {t("gameEd.addCondition")}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button
                    className={addBtnCls}
                    onClick={() =>
                      setAssetBindings([
                        ...assetBindings,
                        {
                          id: `binding.${assetBindings.length + 1}`,
                          slot: "background",
                          assetKey: assetKeys[0] ?? "",
                          when: [],
                        },
                      ])
                    }
                  >
                    + 장면 바인딩 추가
                  </button>
                </div>
              </div>
            </div>
          </div>

          </AdvancedDetails>

          {/* 종료 조건 */}
          <div>
            <SectionTitle
              title={t("gameEd.endConditions")}
              hint={t("gameEd.endConditionsHint")}
            />
            <div className="space-y-3">
              {endConds.map((c, i) => (
                <div key={i} className="rounded-lg border border-line bg-bg-soft p-2">
                  <div className="mb-2 flex items-center gap-2">
                    <KeySelect
                      value={c.key}
                      keys={stateKeys}
                      placeholder={t("gameEd.phKey")}
                      onChange={(key) => updateAt(endConds, setEndConds, i, { key })}
                    />
                    <select
                      className={`${inputCls} w-28`}
                      value={c.op}
                      onChange={(e) =>
                        updateAt(endConds, setEndConds, i, {
                          op: e.target.value as GameConditionOp,
                        })
                      }
                    >
                      {CONDITION_OPS.map((op) => (
                        <option key={op} value={op}>
                          {conditionOpLabel(op, t)}
                        </option>
                      ))}
                    </select>
                    {c.op !== "truthy" && c.op !== "falsy" && (
                      <input
                        className={`${inputCls} w-20`}
                        value={String(c.value ?? "")}
                        placeholder={t("gameEd.phValue")}
                        onChange={(e) =>
                          updateAt(endConds, setEndConds, i, { value: coerceValue(e.target.value) })
                        }
                      />
                    )}
                    <button
                      className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                      onClick={() => setEndConds(endConds.filter((_, j) => j !== i))}
                      aria-label={t("common.delete")}
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <input
                      className={`${inputCls} flex-1`}
                      value={c.label ?? ""}
                      placeholder={t("gameEd.phEndingName")}
                      onChange={(e) => updateAt(endConds, setEndConds, i, { label: e.target.value })}
                    />
                    <select
                      className={`${inputCls} w-28`}
                      value={c.tone ?? "info"}
                      onChange={(e) =>
                        updateAt(endConds, setEndConds, i, {
                          tone: e.target.value as GameEndCondition["tone"],
                        })
                      }
                    >
                      {TONES.map((tone) => (
                        <option key={tone} value={tone}>
                          {toneLabel(tone, t)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <input
                    className={`${inputCls} mt-2`}
                    value={c.notice ?? ""}
                    placeholder={t("gameEd.phEndingNotice")}
                    onChange={(e) => updateAt(endConds, setEndConds, i, { notice: e.target.value })}
                  />
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() =>
                  setEndConds([
                    ...endConds,
                    { key: stateKeys[0] ?? "", op: "lte", value: 0, label: "" },
                  ])
                }
              >
                {t("gameEd.addEndCondition")}
              </button>
            </div>
          </div>

          {/* 위젯 바인딩 */}
          <div>
            <SectionTitle title={t("gameEd.widgets")} hint={t("gameEd.widgetsHint")} />

            {/* 스탯 바 */}
            <div className="mb-3 space-y-2">
              <div className="text-xs font-medium text-ink-soft">{t("gameEd.statBars")}</div>
              {statBars.map((b, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className={`${inputCls} w-24`}
                    value={b.label}
                    placeholder={t("gameEd.phLabel")}
                    onChange={(e) => updateAt(statBars, setStatBars, i, { label: e.target.value })}
                  />
                  <KeySelect
                    value={b.valueKey}
                    keys={stateKeys}
                    placeholder={t("gameEd.phValueKey")}
                    onChange={(valueKey) => updateAt(statBars, setStatBars, i, { valueKey })}
                  />
                  <KeySelect
                    value={b.maxKey ?? ""}
                    keys={stateKeys}
                    placeholder={t("gameEd.phMaxKey")}
                    onChange={(maxKey) => updateAt(statBars, setStatBars, i, { maxKey })}
                  />
                  <select
                    className={`${inputCls} w-24`}
                    value={b.tone ?? ""}
                    onChange={(e) => updateAt(statBars, setStatBars, i, { tone: e.target.value })}
                  >
                    <option value="">{t("common.default")}</option>
                    <option value="hp">{t("gameEd.toneHp")}</option>
                    <option value="mp">{t("gameEd.toneMp")}</option>
                    <option value="affection">{t("gameEd.toneAffection")}</option>
                  </select>
                  <button
                    className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                    onClick={() => setStatBars(statBars.filter((_, j) => j !== i))}
                    aria-label={t("common.delete")}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() =>
                  setStatBars([
                    ...statBars,
                    { label: "", valueKey: stateKeys[0] ?? "", tone: "hp" },
                  ])
                }
              >
                {t("gameEd.addStatBar")}
              </button>
            </div>

            {/* 스탯 패널 */}
            <div className="mb-3 space-y-2">
              <div className="text-xs font-medium text-ink-soft">{t("gameEd.statPanel")}</div>
              {statPanel.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    className={`${inputCls} w-28`}
                    value={p.label}
                    placeholder={t("gameEd.phLabel")}
                    onChange={(e) => updateAt(statPanel, setStatPanel, i, { label: e.target.value })}
                  />
                  <KeySelect
                    value={p.key}
                    keys={stateKeys}
                    placeholder={t("gameEd.phKey")}
                    onChange={(key) => updateAt(statPanel, setStatPanel, i, { key })}
                  />
                  <button
                    className="shrink-0 rounded px-2 py-1 text-xs text-red-400 hover:bg-bg-raised"
                    onClick={() => setStatPanel(statPanel.filter((_, j) => j !== i))}
                    aria-label={t("common.delete")}
                  >
                    <X size={14} aria-hidden="true" />
                  </button>
                </div>
              ))}
              <button
                className={addBtnCls}
                onClick={() => setStatPanel([...statPanel, { label: "", key: stateKeys[0] ?? "" }])}
              >
                {t("gameEd.addPanelItem")}
              </button>
            </div>

            {/* 인벤토리 + 토글 */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                {t("gameEd.inventoryKey")}
                <KeySelect
                  value={inventoryKey}
                  keys={["", ...stateKeys]}
                  placeholder={t("gameEd.none")}
                  onChange={setInventoryKey}
                />
              </label>
              {inventoryKey.trim() && (
                <label className="flex items-center gap-2 text-sm text-ink-soft">
                  {t("gameEd.inventoryTitle")}
                  <input
                    className={`${inputCls} w-32`}
                    value={inventoryTitle}
                    placeholder={t("gameEd.defaultInventory")}
                    onChange={(e) => setInventoryTitle(e.target.value)}
                  />
                </label>
              )}
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={showChoices}
                  onChange={(e) => setShowChoices(e.target.checked)}
                />
                {t("gameEd.showChoices")}
              </label>
              <label className="flex items-center gap-2 text-sm text-ink-soft">
                <input
                  type="checkbox"
                  checked={showDice}
                  onChange={(e) => setShowDice(e.target.checked)}
                />
                {t("gameEd.showDice")}
              </label>
            </div>
          </div>

          {/* 공유/출처 정보(결정 #23). 내보내기·향후 허브 공유에 함께 담긴다. */}
          <div>
            <SectionTitle
              title={t("gameEd.share")}
              hint={t("gameEd.shareHint")}
            />
            <div className="space-y-3">
              <Field label={t("gameEd.author")}>
                <input
                  className={inputCls}
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder={t("gameEd.phAuthor")}
                />
              </Field>
              <Field label={t("gameEd.license")}>
                <input
                  className={inputCls}
                  value={license}
                  onChange={(e) => setLicense(e.target.value)}
                  placeholder={t("gameEd.phLicense")}
                />
              </Field>
              <Field label={t("gameEd.tags")} hint={t("gameEd.tagsHint")}>
                <input
                  className={inputCls}
                  value={tagsText}
                  onChange={(e) => setTagsText(e.target.value)}
                  placeholder={t("gameEd.phTags")}
                />
              </Field>
              <Field label={t("gameEd.rating")}>
                <select
                  className={inputCls}
                  value={rating}
                  onChange={(e) => setRating(e.target.value as ContentRating)}
                >
                  <option value="unrated">{t("gameEd.ratingUnrated")}</option>
                  <option value="sfw">{t("gameEd.ratingSfw")}</option>
                  <option value="nsfw">{t("gameEd.ratingNsfw")}</option>
                </select>
              </Field>
            </div>
          </div>

          {/* 활성 토글 */}
          <label className="flex items-center gap-2 border-t border-line pt-4 text-sm text-ink-soft">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            {t("gameEd.enabledInNewChats")}
          </label>

          {error && (
            <div className="rounded-lg border border-red-800 bg-red-950/40 px-3 py-2 text-xs text-red-300">
              {t(error as any)}
            </div>
          )}
        </div>
        )}

        {/* 제작 린트 경고(결정 #30): 컴파일은 되지만 의도와 어긋날 가능성을 알려준다. */}
        {lintIssues.length > 0 && (
          <div className="shrink-0 space-y-1 border-t border-line bg-bg-soft px-3 py-2">
            {lintIssues.map((issue, i) => (
              <div
                key={i}
                className={[
                  "flex items-start gap-1.5 text-xs",
                  issue.severity === "error" ? "text-red-300" : issue.severity === "warning" ? "text-yellow-300" : "text-ink-faint",
                ].join(" ")}
              >
                {issue.severity === "error" ? (
                  <OctagonAlert size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                ) : issue.severity === "warning" ? (
                  <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
                ) : (
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
                )}
                <span className="min-w-0">{t(issue.message as any, issue.args)}</span>
              </div>
            ))}
          </div>
        )}

        {/* 푸터 */}
        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-line p-3">
          <button
            className="rounded-lg px-3 py-1.5 text-sm text-ink-soft hover:bg-bg-raised"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="rounded-lg bg-accent px-4 py-1.5 text-sm font-medium text-white hover:bg-accent-soft disabled:opacity-50"
            onClick={() => save()}
            disabled={busy}
          >
            {busy ? t("common.saving") : t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- 작은 보조 컴포넌트/헬퍼 ----

const inputCls =
  "rounded-lg border border-line bg-bg-soft px-2 py-1.5 text-sm text-ink outline-none focus:border-accent";
const addBtnCls =
  "rounded-lg border border-dashed border-line px-3 py-1.5 text-xs text-ink-soft hover:border-accent hover:text-ink";

function DocumentIssueList({
  title,
  issues,
}: {
  title: string;
  issues: RoqeetGameDocumentIssue[];
}) {
  return (
    <div className="max-h-32 shrink-0 overflow-y-auto rounded-lg border border-line bg-bg-soft px-3 py-2 scroll-touch">
      <div className="mb-1 text-xs font-semibold text-ink">{title}</div>
      <div className="space-y-1">
        {issues.map((issue, index) => (
          <div
            key={`${issue.code}-${issue.path ?? index}`}
            className={[
              "flex items-start gap-1.5 text-xs",
              issue.severity === "error" ? "text-red-300" : issue.severity === "warning" ? "text-yellow-300" : "text-ink-faint",
            ].join(" ")}
          >
            {issue.severity === "error" ? (
              <OctagonAlert size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
            ) : issue.severity === "warning" ? (
              <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
            ) : (
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-current" aria-hidden="true" />
            )}
            <span className="min-w-0 break-words">
              {issue.path ? `${issue.path}: ${issue.message}` : issue.message}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-ink">{label}</span>
      {hint && <span className="mb-1 block text-xs text-ink-faint">{hint}</span>}
      <div className="flex flex-col">{children}</div>
    </label>
  );
}

function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="mb-2">
      <h3 className="text-sm font-semibold text-ink">{title}</h3>
      {hint && <p className="text-xs text-ink-faint">{hint}</p>}
    </div>
  );
}

/** 상태 키 후보를 드롭다운으로 + 직접 입력 폴백(datalist). */
function AdvancedDetails({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <details className="rounded-lg border border-line bg-bg-soft px-3 py-2">
      <summary className="cursor-pointer text-sm font-semibold text-ink">{title}</summary>
      {hint && <p className="mt-1 text-xs text-ink-faint">{hint}</p>}
      <div className="mt-3 space-y-5">{children}</div>
    </details>
  );
}

function KeySelect({
  value,
  keys,
  placeholder,
  onChange,
}: {
  value: string;
  keys: string[];
  placeholder?: string;
  onChange: (v: string) => void;
}) {
  const listId = useMemo(() => `keys-${Math.random().toString(36).slice(2)}`, []);
  return (
    <>
      <input
        className={`${inputCls} flex-1`}
        value={value}
        list={listId}
        placeholder={placeholder ?? "key"}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {keys.filter(Boolean).map((k) => (
          <option key={k} value={k} />
        ))}
      </datalist>
    </>
  );
}

function mergeFormStateSchema(
  initialState: Record<string, unknown>,
  existing: GameStateFieldSchema[] | undefined,
): GameStateFieldSchema[] {
  const byKey = new Map((existing ?? []).map((field) => [field.key, field]));
  const fields: GameStateFieldSchema[] = [];
  for (const [key, value] of Object.entries(initialState)) {
    const found = byKey.get(key);
    if (found) {
      fields.push(found);
      continue;
    }
    fields.push(inferFormStateField(key, value));
  }
  return fields;
}

function inferFormStateField(key: string, value: unknown): GameStateFieldSchema {
  if (key === "completed" || key === "failed" || key === "ended") {
    return { key, valueType: "boolean", default: false, mutableBy: "engine", derived: true };
  }
  if (/^max[A-Z]/.test(key) || /Goal$/.test(key)) {
    return { key, valueType: "number", default: typeof value === "number" ? value : 0, mutableBy: "readonly" };
  }
  if (Array.isArray(value)) {
    return {
      key,
      valueType: value.every((item) => typeof item === "string") ? "string[]" : "array",
      default: value,
      mutableBy: "ai",
    };
  }
  if (typeof value === "number") return { key, valueType: "number", default: value, mutableBy: "ai" };
  if (typeof value === "boolean") return { key, valueType: "boolean", default: value, mutableBy: "ai" };
  return { key, valueType: "string", default: String(value ?? ""), mutableBy: "ai" };
}

function initStateRows(def: GameDefinition | undefined, defaultLocation: string): StateRow[] {
  if (!def) {
    // 기본 시드: 흔한 어드벤처형 골격.
    return [
      { key: "hp", value: "100", type: "number" },
      { key: "maxHp", value: "100", type: "number" },
      { key: "location", value: defaultLocation, type: "text" },
      { key: "inventory", value: "", type: "list" },
    ];
  }
  return Object.entries(def.initialState).map(([key, v]) => {
    if (Array.isArray(v)) return { key, value: "", type: "list" as const };
    if (typeof v === "boolean") return { key, value: v ? "true" : "false", type: "boolean" as const };
    if (typeof v === "number") return { key, value: String(v), type: "number" as const };
    return { key, value: String(v ?? ""), type: "text" as const };
  });
}

function rowsToState(rows: StateRow[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (!key) continue;
    switch (row.type) {
      case "list":
        out[key] = [];
        break;
      case "boolean":
        out[key] = row.value === "true";
        break;
      case "number": {
        const n = Number(row.value);
        out[key] = Number.isFinite(n) ? n : 0;
        break;
      }
      default:
        out[key] = row.value;
    }
  }
  return out;
}

function pickAdvancedDefinition(def: GameDefinition | undefined): Partial<GameDefinition> {
  if (!def) return {};
  return {
    ...(def.titleKey !== undefined ? { titleKey: def.titleKey } : {}),
    ...(def.descriptionKey !== undefined ? { descriptionKey: def.descriptionKey } : {}),
    ...(def.instructionsKey !== undefined ? { instructionsKey: def.instructionsKey } : {}),
    ...(def.stateSummaryTemplateKey !== undefined
      ? { stateSummaryTemplateKey: def.stateSummaryTemplateKey }
      : {}),
    ...(def.stateSchema?.length ? { stateSchema: def.stateSchema } : {}),
    ...(def.setup?.length ? { setup: def.setup } : {}),
    ...(def.entityTemplate ? { entityTemplate: def.entityTemplate } : {}),
    ...(def.entityRoster ? { entityRoster: def.entityRoster } : {}),
    ...(def.mutableEntityKeys?.length ? { mutableEntityKeys: def.mutableEntityKeys } : {}),
    ...(def.entityClamps?.length ? { entityClamps: def.entityClamps } : {}),
    ...(def.initRules?.length ? { initRules: def.initRules } : {}),
    ...(def.sceneGraph?.scenes.length ? { sceneGraph: def.sceneGraph } : {}),
    ...(def.hud ? { hud: def.hud } : {}),
  };
}

function updateRow(
  rows: StateRow[],
  set: (rows: StateRow[]) => void,
  i: number,
  patch: Partial<StateRow>,
) {
  set(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
}

function updateAt<T>(arr: T[], set: (arr: T[]) => void, i: number, patch: Partial<T>) {
  set(arr.map((item, j) => (j === i ? { ...item, ...patch } : item)));
}

function parseOptNum(v: string): number | undefined {
  const t = v.trim();
  if (!t) return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
}

/** 종료 조건 비교값: 숫자/불린/문자 추론. */
function coerceValue(v: string): number | string | boolean {
  if (v === "true") return true;
  if (v === "false") return false;
  if (v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return v;
}

// ---- 조건부 규칙(결정 #34) 불변 업데이트 헬퍼 ----

/** 저장 가능한 규칙만 거른다(키가 있는 효과가 최소 1개). 빈 껍데기 규칙 제외. */
function validRules(rules: GameRule[]): GameRule[] {
  return rules
    .map((r) => ({
      when: r.when.filter((c) => c.key.trim()),
      then: r.then.filter((a) => a.key.trim()),
    }))
    .filter((r) => r.then.length > 0);
}

function patchCond(
  rules: GameRule[],
  ri: number,
  ci: number,
  patch: Partial<GameCondition>,
): GameRule[] {
  return rules.map((r, j) =>
    j === ri ? { ...r, when: r.when.map((c, k) => (k === ci ? { ...c, ...patch } : c)) } : r,
  );
}

function removeCond(rules: GameRule[], ri: number, ci: number): GameRule[] {
  return rules.map((r, j) =>
    j === ri ? { ...r, when: r.when.filter((_, k) => k !== ci) } : r,
  );
}

function addCond(rules: GameRule[], ri: number, cond: GameCondition): GameRule[] {
  return rules.map((r, j) => (j === ri ? { ...r, when: [...r.when, cond] } : r));
}

function patchAction(
  rules: GameRule[],
  ri: number,
  ai: number,
  patch: Partial<GameRuleAction>,
): GameRule[] {
  return rules.map((r, j) =>
    j === ri ? { ...r, then: r.then.map((a, k) => (k === ai ? { ...a, ...patch } : a)) } : r,
  );
}

function removeAction(rules: GameRule[], ri: number, ai: number): GameRule[] {
  return rules.map((r, j) =>
    j === ri ? { ...r, then: r.then.filter((_, k) => k !== ai) } : r,
  );
}

function addAction(rules: GameRule[], ri: number, action: GameRuleAction): GameRule[] {
  return rules.map((r, j) => (j === ri ? { ...r, then: [...r.then, action] } : r));
}

// ---- 조건부 AI 지시문/선택지 제어(게이트) 불변 업데이트 헬퍼 ----

function validGates(gates: GameGate[]): GameGate[] {
  return gates
    .map((gate) => ({
      when: gate.when.filter((c) => c.key.trim()),
      inject: gate.inject.trim(),
    }))
    .filter((gate) => gate.inject.length > 0);
}

function patchGate(gates: GameGate[], gi: number, patch: Partial<GameGate>): GameGate[] {
  return gates.map((gate, j) => (j === gi ? { ...gate, ...patch } : gate));
}

function patchGateCond(
  gates: GameGate[],
  gi: number,
  ci: number,
  patch: Partial<GameCondition>,
): GameGate[] {
  return gates.map((gate, j) =>
    j === gi
      ? { ...gate, when: gate.when.map((c, k) => (k === ci ? { ...c, ...patch } : c)) }
      : gate,
  );
}

function removeGateCond(gates: GameGate[], gi: number, ci: number): GameGate[] {
  return gates.map((gate, j) =>
    j === gi ? { ...gate, when: gate.when.filter((_, k) => k !== ci) } : gate,
  );
}

function addGateCond(gates: GameGate[], gi: number, cond: GameCondition): GameGate[] {
  return gates.map((gate, j) => (j === gi ? { ...gate, when: [...gate.when, cond] } : gate));
}

// ---- 에셋-장면 바인딩 불변 업데이트 헬퍼 ----

function validAssets(assets: GameAssetRegistryEntry[]): GameAssetRegistryEntry[] {
  return assets
    .map((asset) => {
      const out: GameAssetRegistryEntry = {
        key: asset.key.trim(),
        kind: ASSET_KINDS.includes(asset.kind) ? asset.kind : "image",
      };
      const assetId = asset.assetId?.trim();
      const label = asset.label?.trim();
      if (assetId) out.assetId = assetId;
      if (label) out.label = label;
      return out;
    })
    .filter((asset) => asset.key.length > 0);
}

function validAssetBindings(bindings: GameAssetBinding[]): GameAssetBinding[] {
  return bindings
    .map((binding) => {
      const out: GameAssetBinding = {
        id: binding.id.trim(),
        slot: ASSET_SLOTS.includes(binding.slot) ? binding.slot : "background",
      };
      const assetKey = binding.assetKey?.trim();
      const assetId = binding.assetId?.trim();
      const characterId = binding.characterId?.trim();
      const emotion = binding.emotion?.trim();
      const when = (binding.when ?? []).filter((cond) => cond.key.trim());
      if (assetKey) out.assetKey = assetKey;
      if (assetId) out.assetId = assetId;
      if (when.length) out.when = when;
      if (characterId) out.characterId = characterId;
      if (emotion) out.emotion = emotion;
      if (binding.position && ASSET_POSITIONS.includes(binding.position)) {
        out.position = binding.position;
      }
      return out;
    })
    .filter((binding) => binding.id.length > 0 || !!binding.assetKey || !!binding.assetId);
}

function patchAssetBinding(
  bindings: GameAssetBinding[],
  bi: number,
  patch: Partial<GameAssetBinding>,
): GameAssetBinding[] {
  return bindings.map((binding, j) => (j === bi ? { ...binding, ...patch } : binding));
}

function patchAssetBindingCond(
  bindings: GameAssetBinding[],
  bi: number,
  ci: number,
  patch: Partial<GameCondition>,
): GameAssetBinding[] {
  return bindings.map((binding, j) =>
    j === bi
      ? {
          ...binding,
          when: (binding.when ?? []).map((cond, k) => (k === ci ? { ...cond, ...patch } : cond)),
        }
      : binding,
  );
}

function removeAssetBindingCond(
  bindings: GameAssetBinding[],
  bi: number,
  ci: number,
): GameAssetBinding[] {
  return bindings.map((binding, j) =>
    j === bi ? { ...binding, when: (binding.when ?? []).filter((_, k) => k !== ci) } : binding,
  );
}

function addAssetBindingCond(
  bindings: GameAssetBinding[],
  bi: number,
  cond: GameCondition,
): GameAssetBinding[] {
  return bindings.map((binding, j) =>
    j === bi ? { ...binding, when: [...(binding.when ?? []), cond] } : binding,
  );
}

function assetKindLabel(kind: GameAssetRegistryEntry["kind"]): string {
  switch (kind) {
    case "image":
      return "이미지";
    case "audio":
      return "오디오";
    case "sfx":
      return "효과음";
    case "video":
      return "비디오";
    default:
      return "기타";
  }
}

function assetSlotLabel(slot: GameAssetBinding["slot"]): string {
  switch (slot) {
    case "background":
      return "배경";
    case "sprite":
      return "스프라이트";
    case "bgm":
      return "BGM";
    case "sfx":
      return "효과음";
    default:
      return "오버레이";
  }
}

function assetPositionLabel(position: NonNullable<GameAssetBinding["position"]>): string {
  switch (position) {
    case "left":
      return "왼쪽";
    case "right":
      return "오른쪽";
    default:
      return "가운데";
  }
}

function conditionOpLabel(op: GameConditionOp, t: Translate): string {
  switch (op) {
    case "lte":
      return t("gameEd.opLte");
    case "gte":
      return t("gameEd.opGte");
    case "lt":
      return t("gameEd.opLt");
    case "gt":
      return t("gameEd.opGt");
    case "eq":
      return t("gameEd.opEq");
    case "neq":
      return t("gameEd.opNeq");
    case "truthy":
      return t("gameEd.opTruthy");
    case "falsy":
      return t("gameEd.opFalsy");
    default:
      return op;
  }
}

function toneLabel(tone: (typeof TONES)[number], t: Translate): string {
  switch (tone) {
    case "success":
      return t("gameEd.toneSuccess");
    case "warning":
      return t("gameEd.toneWarning");
    case "danger":
      return t("gameEd.toneDanger");
    default:
      return t("gameEd.toneInfo");
  }
}

function defaultInstructions(t: Translate): string {
  return [
    t("gameEd.defaultInstruction1"),
    "당신은 규칙을 집행하지 않습니다. 장면을 서술하고, 필요할 때만 가능한 다음 행동을 제안하세요.",
    "상태 변경·주사위·종료 판정은 RoQeet Engine이 선언 노드와 action 계약으로 검증해 적용합니다.",
  ].join("\n");
}
